import { PERMIT2_ABI } from '../constants/abis.js';
import { permit2Address } from '../constants/addresses.js';
import { BaseError } from '../errors/base.js';
import { SDKReferenceError } from '../errors/primitives.js';
import { MAX_UINT160 } from '../index.js';
import { ContextHost } from '../service/contextHost.js';
import { PermitSource } from '../types/permit2.js';
import { encodeFunctionData, encodePacked, parseAbi, } from 'viem';
export class Permit extends ContextHost {
    constructor(config, injected) {
        super(config, { publicClient: injected.publicClient });
        Object.defineProperty(this, "walletClient", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "getTokenAllowance", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: async ({ account, token, spender, }) => {
                const allowance = await this.publicClient.readContract({
                    address: token,
                    abi: [
                        {
                            type: 'function',
                            name: 'allowance',
                            stateMutability: 'view',
                            inputs: [
                                {
                                    name: 'owner',
                                    type: 'address',
                                },
                                {
                                    name: 'spender',
                                    type: 'address',
                                },
                            ],
                            outputs: [{ type: 'uint256' }],
                        },
                    ],
                    functionName: 'allowance',
                    args: [account, spender],
                });
                return allowance;
            }
        });
        Object.defineProperty(this, "getTokenPermit", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: async ({ account, token, spender, }) => {
                if (!permit2Address[this.chainId]) {
                    throw new SDKReferenceError('chainId', 'chainId not supported');
                }
                const [amount, expiration, nonce] = await this.publicClient.readContract({
                    address: permit2Address[this.chainId],
                    abi: PERMIT2_ABI,
                    functionName: 'allowance',
                    args: [account, token, spender],
                });
                return {
                    amount,
                    expiration,
                    nonce,
                };
            }
        });
        Object.defineProperty(this, "getAllowance", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: async (params) => {
                const [allowance, { amount, expiration, nonce }] = await Promise.all([
                    this.getTokenAllowance({
                        ...params,
                        spender: permit2Address[this.chainId],
                    }),
                    this.getTokenPermit(params),
                ]);
                return {
                    amount,
                    expiration,
                    nonce,
                    allowance,
                };
            }
        });
        Object.defineProperty(this, "get712Domain", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => {
                return {
                    name: 'Permit2',
                    chainId: this.chainId,
                    verifyingContract: this.permit2ContractConfig().address,
                };
            }
        });
        Object.defineProperty(this, "permit2ContractConfig", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => {
                if (!permit2Address[this.chainId]) {
                    throw new SDKReferenceError('chainId', 'chainId not supported');
                }
                return {
                    address: permit2Address[this.chainId],
                    abi: PERMIT2_ABI,
                };
            }
        });
        Object.defineProperty(this, "get712Types", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => {
                return {
                    PermitSingle: [
                        { name: 'details', type: 'PermitDetails' },
                        { name: 'spender', type: 'address' },
                        { name: 'sigDeadline', type: 'uint256' },
                    ],
                    PermitDetails: [
                        { name: 'token', type: 'address' },
                        { name: 'amount', type: 'uint160' },
                        { name: 'expiration', type: 'uint48' },
                        { name: 'nonce', type: 'uint48' },
                    ],
                };
            }
        });
        Object.defineProperty(this, "signSinglePermit", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: async (message, account) => {
                const domain = this.get712Domain();
                const types = this.get712Types();
                const primaryType = 'PermitSingle';
                const permitSig = await this.walletClient.signTypedData({
                    domain,
                    types,
                    primaryType,
                    message,
                    account,
                });
                const address = typeof account === 'string' ? account : account.address;
                const valid = await this.publicClient.verifyTypedData({
                    address,
                    domain,
                    types,
                    primaryType,
                    message,
                    signature: permitSig,
                });
                if (!valid) {
                    throw new BaseError('PermitSingle Signature is invalid');
                }
                return permitSig;
            }
        });
        Object.defineProperty(this, "signTakerPermit", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: async (message, account) => {
                const permitSig = await this.signSinglePermit(message, account);
                const address = typeof account === 'string' ? account : account?.address;
                const sigWithSelector = encodeFunctionData({
                    ...this.permit2ContractConfig(),
                    abi: PERMIT2_ABI,
                    functionName: 'permit',
                    args: [address, message, permitSig],
                });
                const sig = `0x${sigWithSelector.slice('0x2b67b570'.length)}`;
                return encodePacked(['uint8', 'bytes'], [PermitSource.Permit2AllowanceTransfer, sig]);
            }
        });
        Object.defineProperty(this, "approve", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: async ({ spender, amount, token, }, account) => {
                const tx = await this.walletClient.writeContract({
                    address: token,
                    abi: parseAbi([
                        'function approve(address spender, uint256 amount) returns (bool)',
                    ]),
                    functionName: 'approve',
                    args: [spender, amount],
                    account: account ?? this.walletClient.account,
                    chain: this.walletClient.chain,
                });
                return tx;
            }
        });
        Object.defineProperty(this, "approveWithReceipt", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: async ({ spender, amount, token, }, account) => {
                const tx = await this.approve({ spender, amount, token }, account);
                const receipt = await this.publicClient.waitForTransactionReceipt({
                    hash: tx,
                });
                return receipt;
            }
        });
        Object.defineProperty(this, "approveSpenderContract", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: async ({ spender, expiration, token, }, account) => {
                const tx = await this.walletClient.writeContract({
                    address: permit2Address[this.chainId],
                    abi: PERMIT2_ABI,
                    functionName: 'approve',
                    args: [token, spender, MAX_UINT160, expiration],
                    account: account ?? this.walletClient.account?.address,
                    chain: this.walletClient.chain,
                });
                return tx;
            }
        });
        Object.defineProperty(this, "approveSpenderContractWithReceipt", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: async ({ spender, expiration, token, }, account) => {
                const tx = await this.approveSpenderContract({
                    spender,
                    expiration,
                    token,
                }, account);
                const receipt = await this.publicClient.waitForTransactionReceipt({
                    hash: tx,
                });
                return receipt;
            }
        });
        Object.defineProperty(this, "revokeSpenderContract", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: async ({ spender, token, }, account) => {
                const tx = await this.walletClient.writeContract({
                    address: permit2Address[this.chainId],
                    abi: PERMIT2_ABI,
                    functionName: 'lockdown',
                    args: [[{ token: token, spender: spender }]],
                    account: account ?? this.walletClient.account?.address,
                    chain: this.walletClient.chain,
                });
                return tx;
            }
        });
        Object.defineProperty(this, "revokeSpenderContractWithReceipt", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: async ({ spender, token, }, account) => {
                const tx = await this.revokeSpenderContract({
                    spender,
                    token,
                }, account);
                const receipt = await this.publicClient.waitForTransactionReceipt({
                    hash: tx,
                });
                return receipt;
            }
        });
        this.walletClient = injected.walletClient;
    }
}
//# sourceMappingURL=index.js.map